// Mapa trasa → sekcja dostępu — JEDYNE miejsce, które wie, która strona należy do
// której sekcji katalogu (backend/app/auth.py `SECTIONS`). Czytają ją: bramka
// i filtr zakładek w AppShell, pigułki SubTabLayout / RaportyTabsLayout,
// przekierowanie `/`. Nowa trasa w main.tsx = nowy wpis tutaj (pilnuje tego
// access.test.ts, który czyta trasy wprost z main.tsx i klucze z auth.py).
//
// Czyste funkcje (bez Reacta) — o fladze AUTH_REQUIRED decyduje warstwa wyżej
// (lib/session.ts: useAccess / useCan); tu „czy to konto widzi tę ścieżkę".

import { hasAccess, isAdmin, type Me } from './auth'

interface RouteRule {
  // Wzorzec ścieżki: segmenty, `:param` = jeden dowolny segment, końcowe `/*` =
  // sama baza albo cokolwiek pod nią.
  pattern: string
  // Wystarczy dostęp do KTÓREJKOLWIEK (Motoryka = analityka + zarządzanie danymi
  // na jednej trasie; które wewnętrzne zakładki widać, rozstrzyga sama strona).
  sections: string[]
}

// Kolejność ma znaczenie: pierwsza pasująca reguła wygrywa, więc węższe
// (/raporty/live/*) stoją przed szerszymi (/raporty/*).
const RULES: RouteRule[] = [
  { pattern: '/raporty/*', sections: ['raporty.mecze'] }, // lista + /raporty/:matchId
  // Analiza przeciwnika
  { pattern: '/dossier/*', sections: ['analiza.druzyny'] },
  { pattern: '/gracze/*', sections: ['analiza.zawodnicy'] },
  // Motoryka
  { pattern: '/motoryka/*', sections: ['motoryka.analityka', 'motoryka.dane'] },
  // Kadra
  { pattern: '/kadra', sections: ['kadra'] },
]

// Publiczne także przy AUTH_REQUIRED: logowanie / zmiana hasła / ustawienie hasła
// z linku z maila (/konto/ustaw-haslo). /admin NIE jest publiczny — wymaga sesji,
// a samą rolę admina sprawdza strona.
const PUBLIC_PATTERNS = ['/konto/*']

// Kolejność „pierwszej dostępnej sekcji" dla `/` i linków zakładek-parasoli:
// najpierw dzisiejszy start (lista meczów), dalej kolejność zakładek AppShell.
export const LANDING_PATHS: string[] = [
  '/raporty', '/dossier', '/gracze', '/motoryka', '/kadra',
]

/** Start aplikacji — `/` i nieznane adresy: lista meczów. */
export const DEFAULT_LANDING = '/raporty'

function normalize(path: string): string[] {
  const clean = path.split(/[?#]/)[0]
  return clean.split('/').filter(Boolean)
}

/** Czy `pathname` pasuje do wzorca (`:param`, końcowe `/*`). */
export function matchPattern(pattern: string, pathname: string): boolean {
  const pat = normalize(pattern)
  const segs = normalize(pathname)
  const wildcard = pat[pat.length - 1] === '*'
  const fixed = wildcard ? pat.slice(0, -1) : pat
  if (wildcard ? segs.length < fixed.length : segs.length !== fixed.length) return false
  return fixed.every((p, i) => p.startsWith(':') || p === segs[i])
}

/** Sekcje, z których którakolwiek otwiera ścieżkę; null = ścieżka bez sekcji
 *  (publiczna albo spoza mapy — wtedy nie blokujemy). */
export function sectionsForPath(pathname: string): string[] | null {
  return RULES.find((r) => matchPattern(r.pattern, pathname))?.sections ?? null
}

/** Główna sekcja ścieżki (pierwsza z listy) — np. do komunikatów. */
export function sectionForPath(pathname: string): string | null {
  return sectionsForPath(pathname)?.[0] ?? null
}

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATTERNS.some((p) => matchPattern(p, pathname))
}

/** Czy konto widzi ścieżkę (Podgląd wystarczy). Admin — wszystko; ścieżka bez
 *  sekcji — każdy. Bez konta: tylko ścieżki bez sekcji. Flagi NIE sprawdza. */
export function canView(me: Me | null, pathname: string): boolean {
  const sections = sectionsForPath(pathname)
  if (!sections || isPublicPath(pathname)) return true
  if (!me) return false
  if (isAdmin(me)) return true
  return sections.some((s) => hasAccess(me, s, 'view'))
}

/** Pierwsza dostępna strona startowa (null = konto bez żadnej sekcji). */
export function landingPath(me: Me | null): string | null {
  return LANDING_PATHS.find((p) => canView(me, p)) ?? null
}
